import React from 'react';

import { useMutation } from '@tanstack/react-query';

import { ButtonBase, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { paneTokens } from '~/pages/AskRichardUnified/uiStream/paneTokens';

import { useGetReportingLeLens } from '~/hooks/apis/reporting/useGetReportingLeLens';
import { type LensOperation, lensChangeFailure, useLensChange } from '~/hooks/apis/reporting/useLensChange';

export type LensConflict = { kind: 'lens_conflict'; lensId: string; error: string; operations?: Array<LensOperation> };

export default function LensConflictReceipt({ conflict, onApply }: { conflict: LensConflict; onApply?: () => void }) {
  const theme = useTheme();
  const pane = paneTokens(theme);
  const { data, refetch } = useGetReportingLeLens(conflict.lensId);
  const change = useLensChange(conflict.lensId);
  const canReapply = Boolean((onApply || conflict.operations?.length) && data?.access.canEdit);
  const reapply = useMutation({
    mutationFn: async (operations: Array<LensOperation>) => {
      const current = await refetch({ throwOnError: true });
      return change.mutateAsync({ baseVersion: current.data!.lens.version, operations });
    },
    retry: false,
  });
  return (
    <Stack spacing={0.5} data-testid="lens-change-conflict">
      <Stack direction="row" alignItems="baseline" spacing={1}>
        <Typography sx={{ fontSize: 13, color: 'text.secondary', flex: 1, minWidth: 0 }}>{conflict.error}</Typography>
        {canReapply && !reapply.isSuccess && (
          <ButtonBase
            disabled={reapply.isPending}
            onClick={() => (onApply ? onApply() : conflict.operations && reapply.mutate(conflict.operations))}
            sx={{ fontSize: 12.5, fontWeight: 500, color: pane.accent, borderRadius: 0.5, flex: 'none' }}
          >
            Apply mine
          </ButtonBase>
        )}
      </Stack>
      {reapply.error && (
        <Typography sx={{ fontSize: 12.5, color: 'error.main' }}>{lensChangeFailure(reapply.error)}</Typography>
      )}
    </Stack>
  );
}
